import { ConfigurationRepositoryWorkspacesService } from '@festina/configuration-general-types/configuration/services/ConfigurationRepositoryWorkspacesService'
import type { ConfigurableServer } from '@festina-life/core'
import {
  type QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'

import {
  allConciseWorkspacesKeyPrefix,
  fetchAllConciseWorkspacesKey,
  fetchWorkspaceOperationsKey,
} from '../keys/configuration'
import type { AllConciseWorkspaces } from './useFetchAllConciseWorkspaces'

type DeleteWorkspaceInputArguments = {
  name: string
  server: ConfigurableServer
  shouldSkipCacheInvalidation?: boolean
}

type DeleteWorkspaceContext = {
  previousConciseWorkspaces: AllConciseWorkspaces | undefined
}

export const invalidateWorkspaceCache = ({
  queryClient,
  name,
  server,
}: {
  queryClient: QueryClient
} & Omit<DeleteWorkspaceInputArguments, 'shouldSkipCacheInvalidation'>) => {
  void queryClient.invalidateQueries({
    queryKey: [allConciseWorkspacesKeyPrefix],
  })
  void queryClient.invalidateQueries({
    queryKey: fetchWorkspaceOperationsKey({
      workspaceName: name,
      server,
    }),
  })
}

export const useDeleteConciseWorkspace = (
  availableServers: ConfigurableServer[],
) => {
  const queryClient = useQueryClient()
  const allConciseWorkspacesKey = fetchAllConciseWorkspacesKey(availableServers)

  return useMutation<
    void,
    Error,
    DeleteWorkspaceInputArguments,
    DeleteWorkspaceContext
  >({
    mutationFn: async ({ name, server }) => {
      await ConfigurationRepositoryWorkspacesService.workspaceServiceDeleteDeleteWorkspace(
        { workspaceName: name, componentName: server.toLocaleLowerCase() },
      )
    },
    onMutate: async ({ name, server, shouldSkipCacheInvalidation }) => {
      if (shouldSkipCacheInvalidation) {
        return { previousConciseWorkspaces: undefined }
      }

      // Stop in-flight refetches from overwriting the optimistic update.
      await queryClient.cancelQueries({ queryKey: allConciseWorkspacesKey })

      const previousConciseWorkspaces =
        queryClient.getQueryData<AllConciseWorkspaces>(allConciseWorkspacesKey)

      queryClient.setQueryData<AllConciseWorkspaces>(
        allConciseWorkspacesKey,
        (allConciseWorkspaces) =>
          allConciseWorkspaces && {
            ...allConciseWorkspaces,
            [server]: allConciseWorkspaces[server]?.filter(
              (workspace) => workspace.name !== name,
            ),
          },
      )

      return { previousConciseWorkspaces }
    },
    // oxlint-disable-next-line max-params
    onError: (_, __, context) => {
      if (context?.previousConciseWorkspaces) {
        queryClient.setQueryData(
          allConciseWorkspacesKey,
          context.previousConciseWorkspaces,
        )
      }
    },
    // oxlint-disable-next-line max-params
    onSettled: (_, __, { shouldSkipCacheInvalidation, ...rest }) => {
      if (!shouldSkipCacheInvalidation) {
        void invalidateWorkspaceCache({ queryClient, ...rest })
      }
    },
  })
}
